import { Trash } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSearchProjectMemberManagement } from '@/features/projects/shared/hooks/use-search-project-member-management';
import { ProjectMemberManagementData } from '@/features/projects/shared/types/project-member-management-types';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import {
  StatusIndicator,
  StatusIndicatorType,
} from '@/shared/components/status-indicator';
import { EmptyTablePlaceholder } from '@/shared/components/table/empty-table-placeholder';
import { CustomTable } from '@/shared/components/table/table';
import { CustomTableHeader } from '@/shared/components/table/table-header';
import { CustomTableRow } from '@/shared/components/table/table-row';
import { TableBody } from '@/shared/components/ui/table';
import { formatDate } from '@/shared/utils/format';

export const ProjectMemberManagementTable = ({
  projectId,
  handleDeleteMember,
  onResendInvitation,
  onDeleteInvitation,
}: {
  projectId: string;
  handleDeleteMember: (member: ProjectMemberManagementData) => void;
  onResendInvitation?: (inviteId: number) => void;
  onDeleteInvitation?: (inviteId: number) => void;
}) => {
  const { t } = useTranslation('project', { keyPrefix: 'MemberManagement' });
  const { t: tRoles } = useTranslation('org', { keyPrefix: 'Roles' });
  const { t: tStatus } = useTranslation('shared', {
    keyPrefix: 'InvitationStatus',
  });

  const {
    data: members,
    isLoading,
    error,
  } = useSearchProjectMemberManagement(projectId);

  const columns = [
    t('columns.name'),
    t('columns.status'),
    t('columns.email'),
    t('columns.role'),
    t('columns.member_since'),
  ];

  const columnWidths = {
    name: 'w-[164px]',
    status: 'w-[204px]',
    email: 'w-[242px]',
    role: 'w-[256px]',
    memberSince: 'flex-1 min-w-[156px]',
  };

  return isLoading ? (
    <LoadingIndicator />
  ) : error || !members ? (
    <span>{t('could_not_fetch_members')}</span>
  ) : members.length > 0 ? (
    <CustomTable>
      <CustomTableHeader
        columns={columns}
        columnWidths={Object.values(columnWidths)}
      />
      <TableBody>
        {members.map((member, index) => (
          <CustomTableRow
            key={member.id}
            type="single"
            isFirstRow={index === 0}
            isLastRow={index === members.length - 1}
            showMenu={true}
            actions={[
              ...(member.type === 'member' && member.role === 'member'
                ? [
                    {
                      action: (
                        <>
                          <Trash className="mr-1 h-4 w-4" />
                          {t('actions.delete')}
                        </>
                      ),
                      onClick: () => handleDeleteMember(member),
                    },
                  ]
                : []),
              ...(member.type === 'invite' && onResendInvitation
                ? [
                    {
                      action: <>{t('actions.resend')}</>,
                      onClick: () =>
                        onResendInvitation(Number(member.invitation.id)),
                    },
                    ...(member.invitation.status === 'pending' &&
                    onDeleteInvitation
                      ? [
                          {
                            action: (
                              <>
                                <Trash className="mr-1 h-4 w-4" />
                                {t('actions.delete')}
                              </>
                            ),
                            onClick: () =>
                              onDeleteInvitation(Number(member.invitation.id)),
                          },
                        ]
                      : []),
                  ]
                : []),
            ]}
            props={[
              {
                value: {
                  text: member.name,
                  type: 'string',
                },
                width: columnWidths.name,
              },
              {
                value: {
                  item: (
                    <StatusIndicator
                      text={
                        member.type === 'invite'
                          ? member.invitation.status === 'declined'
                            ? tStatus('rejected')
                            : tStatus('invited')
                          : tStatus('active')
                      }
                      type={
                        member.type === 'invite'
                          ? member.invitation.status === 'declined'
                            ? StatusIndicatorType.NEGATIVE
                            : StatusIndicatorType.WARNING
                          : StatusIndicatorType.POSITIVE
                      }
                    />
                  ),
                  type: 'custom',
                },
                width: columnWidths.status,
              },
              {
                value: {
                  text: member.email,
                  type: 'string',
                },
                width: columnWidths.email,
              },
              {
                value: {
                  text: tRoles(member.role as any),
                  type: 'string',
                },
                width: columnWidths.role,
              },
              {
                value: {
                  text: member.memberSince
                    ? formatDate({
                        date: member.memberSince,
                        formatType: 'digits',
                      })
                    : '',
                  type: 'string',
                },
                width: columnWidths.memberSince,
              },
            ]}
          />
        ))}
      </TableBody>
    </CustomTable>
  ) : (
    <EmptyTablePlaceholder columns={columns} title={t('no_members')} />
  );
};
